import * as React from 'react';
import { Image, MapPin, ExternalLink } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Separator } from './separator.js';
import { Button } from './button.js';

export interface MediaUnitPopupCardLabels {
  close: string;
  details: string;
  maps: string;
  format: string;
  dimensions: string;
  area: string;
  supplier: string;
  catalogPrice: string;
  illumination: string;
  illuminated: string;
  notIlluminated: string;
  noPhoto: string;
  photoAlt: string;
}

export interface MediaUnitPopupCardProps {
  grwId: string;
  formatLabel: string;
  widthM: number | null;
  heightM: number | null;
  areaM2: number | null | undefined;
  supplierName: string | null;
  catalogPrice: number | null;
  illuminated: boolean;
  address: string;
  city: string;
  onDetails: () => void;
  googleMapsUrl?: string;
  photoUrl?: string;
  onClose: () => void;
  labels: MediaUnitPopupCardLabels;
  /** Optional render slot for quick-add action(s), shown after the main action row. */
  quickAddAction?: React.ReactNode;
  /** Optional render slot for nearby POIs section (rendered after actions) */
  nearbyPoisSection?: React.ReactNode;
}

function formatDimensions(w: number | null, h: number | null): string {
  if (w == null || h == null) return '-';
  return `${w} x ${h} m`;
}

function formatArea(area: number | null | undefined): string {
  if (area == null) return '-';
  return `${area.toFixed(2)} m²`;
}

function formatPrice(price: number | null): string {
  if (price == null) return '-';
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
}

function MediaUnitPopupCard({
  grwId,
  formatLabel,
  widthM,
  heightM,
  areaM2,
  supplierName,
  catalogPrice,
  illuminated,
  address,
  city,
  onDetails,
  googleMapsUrl,
  photoUrl,
  onClose,
  labels,
  quickAddAction,
  nearbyPoisSection,
}: MediaUnitPopupCardProps) {
  return (
    <div className="group relative min-w-[280px] max-w-[340px] text-[13px]" data-testid="pin-popup">
      {/* Close button */}
      <button
        type="button"
        data-testid="close-button"
        aria-label={labels.close}
        onClick={onClose}
        className={cn(
          'absolute top-1.5 right-1.5 z-10 h-6 w-6 rounded-full',
          'bg-background/80 backdrop-blur-sm',
          'opacity-70 hover:opacity-100 transition-opacity',
          'inline-flex items-center justify-center',
          'hover:bg-destructive hover:text-white',
        )}
      >
        <span className="sr-only">{labels.close}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-3 w-3"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>

      {/* Photo */}
      {photoUrl ? (
        <div
          className="relative w-full h-[140px] -mx-[10px] -mt-[10px] mb-2"
          style={{ width: 'calc(100% + 20px)' }}
        >
          <img
            data-testid="photo-img"
            src={photoUrl}
            alt={`${labels.photoAlt} ${grwId}`}
            className="w-full h-full object-cover rounded-t-lg"
          />
        </div>
      ) : (
        <div
          data-testid="photo-placeholder"
          className="relative w-full h-[80px] -mx-[10px] -mt-[10px] mb-2 bg-muted/50 flex items-center justify-center rounded-t-lg"
          style={{ width: 'calc(100% + 20px)' }}
        >
          <Image className="h-6 w-6 text-muted-foreground/40" />
          <span className="ml-2 text-[11px] text-muted-foreground/60">{labels.noPhoto}</span>
        </div>
      )}

      {/* ID header */}
      <div className="flex items-center gap-2 mb-2">
        <span className="font-semibold text-[14px] text-foreground truncate">{grwId}</span>
      </div>

      {/* Details grid */}
      <div className="space-y-1.5 text-muted-foreground">
        <div className="flex justify-between">
          <span>{labels.format}</span>
          <span className="text-foreground font-medium">{formatLabel}</span>
        </div>
        <div className="flex justify-between">
          <span>{labels.dimensions}</span>
          <span className="text-foreground">{formatDimensions(widthM, heightM)}</span>
        </div>
        <div className="flex justify-between">
          <span>{labels.area}</span>
          <span className="text-foreground">{formatArea(areaM2)}</span>
        </div>
        <div className="flex justify-between">
          <span>{labels.supplier}</span>
          <span className="text-foreground">{supplierName ?? '-'}</span>
        </div>
        <div className="flex justify-between">
          <span>{labels.catalogPrice}</span>
          <span className="text-foreground font-medium">{formatPrice(catalogPrice)}</span>
        </div>
        <div className="flex justify-between">
          <span>{labels.illumination}</span>
          <span className="text-foreground">
            {illuminated ? labels.illuminated : labels.notIlluminated}
          </span>
        </div>
      </div>

      {/* Address */}
      <Separator className="my-2" />
      <div className="flex items-start gap-1.5 text-muted-foreground">
        <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span className="text-[12px]">
          {address}, {city}
        </span>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-3">
        <div className="flex-1">
          <Button
            variant="default"
            size="xs"
            fullWidth
            data-testid="detail-link"
            onClick={onDetails}
          >
            {labels.details}
          </Button>
        </div>
        {googleMapsUrl ? (
          <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" data-testid="maps-link">
            <Button variant="outline" size="xs">
              <ExternalLink className="h-3 w-3" />
              {labels.maps}
            </Button>
          </a>
        ) : null}
      </div>

      {quickAddAction != null ? <div className="mt-3">{quickAddAction}</div> : null}

      {/* Nearby POIs section (optional render slot) */}
      {nearbyPoisSection != null ? nearbyPoisSection : null}
    </div>
  );
}

export { MediaUnitPopupCard };
